import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "@/hooks/useDebounce";
import { PRIORITY_STATUS_LABEL } from "@/components/priorities/priorityCardTokens";
import { PriorityStatus } from "@/types";

export const PRIORITY_ITEMS_PER_PAGE = 25;
/** Default status filter: everything still in flight. `done` is opt-in so the list stays short. */
export const DEFAULT_STATUS_FILTER: readonly PriorityStatus[] = (Object.keys(PRIORITY_STATUS_LABEL) as PriorityStatus[]).filter((s) => s !== "done");
/** Every status, `done` included — what a "N deliverables" count on the OKR tree actually counts. */
const ALL_STATUS_FILTER: readonly PriorityStatus[] = Object.keys(PRIORITY_STATUS_LABEL) as PriorityStatus[];
export const isDefaultStatusFilter = (s: string[]) =>
  s.length === DEFAULT_STATUS_FILTER.length && DEFAULT_STATUS_FILTER.every((x) => s.includes(x));
/** Only `done` is hidden by default; the empty state offers to reveal it. */
export const isHidingDone = (s: string[]) => !s.includes("done");
export type PriorityViewMode = "all" | "mine";

const pppPidsFromUrl = (params: URLSearchParams) => (params.get("pppPids") ?? "").split(",").filter(Boolean);
const candidatesOnlyFromUrl = (params: URLSearchParams) => params.get("candidate") === "1";

/**
 * Filter state for /priorities. Deep links: `?pppPids=a,b` preselects PPPs (from the OKR/PPP
 * pages), `?candidate=1` opens the Candidates-only view.
 */
export function usePriorityListFilters(currentUserPid?: string) {
    const [searchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 500);
    const [typeFilter, setTypeFilter] = useState("all");
    // A PPP deep link comes from a "N deliverables" count that includes done ones; landing on a
    // list that hides them reads as missing data, so those arrivals start with every status on.
    const [statusFilter, setStatusFilter] = useState<string[]>(() =>
        pppPidsFromUrl(searchParams).length ? [...ALL_STATUS_FILTER] : [...DEFAULT_STATUS_FILTER]);
    const [userFilter, setUserFilter] = useState("all");
    const [teamFilter, setTeamFilter] = useState("all");
    const [pppFilter, setPppFilter] = useState<string[]>(() => pppPidsFromUrl(searchParams));
    const [candidatesOnly, setCandidatesOnly] = useState(() => candidatesOnlyFromUrl(searchParams));
    const [viewMode, setViewMode] = useState<PriorityViewMode>("all");
    const [page, setPage] = useState(1);

    useEffect(() => { setPage(1); }, [debouncedSearch, typeFilter, statusFilter, userFilter, teamFilter, pppFilter, candidatesOnly, viewMode]);

    // Keep deep-link filters in step with the URL after mount (navbar click, copilot stripping its params).
    useEffect(() => {
        const pids = pppPidsFromUrl(searchParams);
        setPppFilter(pids);
        if (pids.length) setStatusFilter([...ALL_STATUS_FILTER]);
        setCandidatesOnly(candidatesOnlyFromUrl(searchParams));
    }, [searchParams]);

    const effectiveUserPid = viewMode === "mine" ? (currentUserPid ?? "all") : userFilter;

    const hasActiveFilters =
        debouncedSearch !== "" || typeFilter !== "all" || !isDefaultStatusFilter(statusFilter) || userFilter !== "all" ||
        teamFilter !== "all" || pppFilter.length > 0 || candidatesOnly || viewMode === "mine";

    const params = useMemo(() => ({
        search: debouncedSearch,
        type: typeFilter,
        statuses: statusFilter,
        userPid: effectiveUserPid,
        teamPid: teamFilter,
        pppPids: pppFilter,
        candidate: candidatesOnly,
        page,
        limit: PRIORITY_ITEMS_PER_PAGE,
    }), [debouncedSearch, typeFilter, statusFilter, effectiveUserPid, teamFilter, pppFilter, candidatesOnly, page]);

    const clear = () => {
        setSearch(""); setTypeFilter("all"); setStatusFilter([...DEFAULT_STATUS_FILTER]); setUserFilter("all"); setTeamFilter("all");
        setPppFilter([]); setCandidatesOnly(false); setViewMode("all");
    };

    return {
        search, setSearch, typeFilter, setTypeFilter, statusFilter, setStatusFilter, userFilter, setUserFilter,
        teamFilter, setTeamFilter, pppFilter, setPppFilter, candidatesOnly, setCandidatesOnly, viewMode, setViewMode,
        page, setPage, params, hasActiveFilters, clear,
    };
}
